// src/pages/platform-admin/SjcPlatformDashboard.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
    useTheme,
    IconButton,
    Divider,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    School,
    CheckCircle,
    SupervisorAccount,
    Analytics,
    Business,
    People,
    Lightbulb,
    CloudUpload,
    AdminPanelSettings
} from '@mui/icons-material';
import useCustomToast from '../../hooks/useToast';
import { APIPlatformDashboard } from '../../apiServices/APIs';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const StatCard = ({ title, value, icon, color, subtext }) => (
    <Card sx={{ 
        height: '100%', 
        borderRadius: 3, 
        borderLeft: `6px solid ${color}`,
        boxShadow: 1,
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-4px)' }
    }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
            <Box sx={{ p: 2, bgcolor: `${color}15`, borderRadius: '50%', color: color, display: 'flex', alignItems: 'center' }}>
                {icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography  sx={{ fontWeight: 'bold' }}>
                    {(value ?? 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                {subtext && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {subtext}
                    </Typography>
                )}
            </Box>
        </CardContent>
    </Card>
);

const PlatformDashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total_institutions: 0,
        total_students: 0,
        total_supervisors: 0,
        total_institution_admins: 0,
        total_research_topics: 0,
        total_research_projects: 0
    });
    
    const showToast = useCustomToast();
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchDashboardData();
        
        const interval = setInterval(() => {
            fetchDashboardData(true);
        }, 120000); // 2 minutes interval refresh
        
        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, []);

    const fetchDashboardData = async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.listPlatformAdminDashboard(APIPlatformDashboard);
            if (result.success && result.data && isMounted.current) {
                // Safely merge incoming data with the default state values
                setStats(prev => ({
                    ...prev,
                    ...result.data
                }));
            } else if (!silent) {
                setError(result.message || "Failed to load platform data");
            }
        } catch (error) {
            if (!silent) {
                setError("Failed to load platform data");
                console.error("Fetch error:", error);
            }
        } finally {
            if (!silent && isMounted.current) {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <LoadingState message="Loading Platform Dashboard..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchDashboardData} title="Unable to Load Dashboard" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <FullWidthContainer >
            <Box sx={{ backgroundColor: 'white', borderRadius: 3, mb: 3 }}>
                <PlatformAdminBar />
                
                {/* Status Header */}
                <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography  sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                ScholarFlow Global Console
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                System Status Overview • Platform-Wide Metrics
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <IconButton 
                                onClick={() => fetchDashboardData()} 
                                color="primary" 
                                size="medium"
                                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
                            >
                                <RefreshIcon />
                            </IconButton>
                            <Chip 
                                icon={<CheckCircle />} 
                                label="Platform Operational" 
                                color="success"
                                variant="outlined"
                                size="medium"
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Metrics Grid */}
            <Grid container spacing={3}>
                {/* Institutions */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Institutions" 
                        value={stats.total_institutions} 
                        icon={<Business fontSize="large" />} 
                        color={theme.palette.primary.main} 
                        subtext="Registered Universities"
                    />
                </Grid>

                {/* Institution Admins */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Institution Admins" 
                        value={stats.total_institution_admins} 
                        icon={<AdminPanelSettings fontSize="large" />} 
                        color={theme.palette.secondary.main} 
                        subtext="University Administrators"
                    />
                </Grid>

                {/* Supervisors */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Supervisors" 
                        value={stats.total_supervisors} 
                        icon={<SupervisorAccount fontSize="large" />} 
                        color={theme.palette.info.main} 
                        subtext="Academic Supervisors"
                    />
                </Grid>

                {/* Students */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Students" 
                        value={stats.total_students} 
                        icon={<People fontSize="large" />} 
                        color={theme.palette.success.main} 
                        subtext="Registered Research Candidates"
                    />
                </Grid>

                {/* Research Topics */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Research Topics" 
                        value={stats.total_research_topics} 
                        icon={<Lightbulb fontSize="large" />} 
                        color={theme.palette.warning.main} 
                        subtext="Proposed Research Area Inquiries"
                    />
                </Grid>

                {/* Research Projects */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard 
                        title="Research Projects" 
                        value={stats.total_research_projects} 
                        icon={<CloudUpload fontSize="large" />} 
                        color={theme.palette.error.main} 
                        subtext="Uploaded Thesis & Work Units"
                    />
                </Grid>
            </Grid>

            {/* Platform Resource Panel */}
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, fontWeight: 'bold' }}>
                    <Analytics color="primary" /> Platform Insights
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Typography color="text.secondary" paragraph>
                            This control console registers and compiles global platform configurations and user roles. 
                            The displayed metrics show consolidated records across all connected institutions. Ensure system databases are operating optimally to keep regional processes unimpeded.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Last Updated Timestamp */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="caption" color="text.secondary">
                    Aggregates processed at: {new Date().toLocaleString()}
                </Typography>
            </Box>
        </FullWidthContainer>
    );
};

export default PlatformDashboard;
